import React, { useState, useEffect, useRef } from 'react';
import { Lock, Play, Pause, ExternalLink, IndianRupee, AlertCircle, Mic2, Plus, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../config/firebase';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { getQuotaStatus } from '../utils/quotaManager';
import VoiceCloningModal from './VoiceCloningModal';

// Sample modal component for voice samples
const SamplesModal = ({ onClose }) => {
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [audioStates, setAudioStates] = useState({});
  const audioRefs = useRef({});
  const progressIntervals = useRef({});

  // Sample speakers with original and cloned audio
  const speakers = [
    {
      id: 'speaker1',
      name: 'Male Speaker 1',
      originalUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Male1.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D',
      clonedUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Male_1_generated.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D'
    },
    {
      id: 'speaker2',
      name: 'Female Speaker 1',
      originalUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Female1.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D',
      clonedUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Female1_generated.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D'
    },
    {
      id: 'speaker3',
      name: 'Male Speaker 2',
      originalUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Male_2.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D',
      clonedUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Male_2_generated.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D'
    },
    {
      id: 'speaker4',
      name: 'Female Speaker 2',
      originalUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Female_2.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D',
      clonedUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/Female_2_generated.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D'
    },
    {
      id: 'speaker5',
      name: 'Child Speaker',
      originalUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/child.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D',
      clonedUrl: 'https://inferencelake.blob.core.windows.net/voicepreviews/Lucid/child_generated.mp3?sp=r&st=2025-04-02T16:57:21Z&se=2025-08-01T00:57:21Z&spr=https&sv=2024-11-04&sr=c&sig=vwANCPd%2BKfPZbHP0bbB73nJCeEIEXTvSUJ3P7Y3WfM0%3D'
    }
  ];

  // Initialize audio states for each speaker
  useEffect(() => {
    const initialAudioStates = {};
    speakers.forEach(speaker => {
      initialAudioStates[`${speaker.id}-original`] = {
        isPlaying: false,
        isLoading: false,
        error: false,
        progress: 0,
        duration: 0,
        currentTime: 0,
        remainingTime: 0
      };

      initialAudioStates[`${speaker.id}-cloned`] = {
        isPlaying: false,
        isLoading: false,
        error: false,
        progress: 0,
        duration: 0,
        currentTime: 0,
        remainingTime: 0
      };
    });

    setAudioStates(initialAudioStates);

    // Select the first speaker by default
    if (speakers.length > 0) {
      setSelectedSpeaker(speakers[0].id);
    }
  }, []);

  // Format time in MM:SS format
  const formatTime = (timeInSeconds) => {
    if (isNaN(timeInSeconds)) return "00:00";
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  // Start progress tracking interval
  const startProgressTracking = (audioId) => {
    // Clear any existing interval
    if (progressIntervals.current[audioId]) {
      clearInterval(progressIntervals.current[audioId]);
    }

    const audio = audioRefs.current[audioId];
    if (!audio) return;

    // Update initial duration and remaining time
    setAudioStates(prevState => ({
      ...prevState,
      [audioId]: {
        ...prevState[audioId],
        duration: audio.duration || 0,
        remainingTime: audio.duration || 0
      }
    }));

    // Set up interval to track progress
    progressIntervals.current[audioId] = setInterval(() => {
      if (audio && !audio.paused) {
        const progress = audio.currentTime / (audio.duration || 1);
        const remainingTime = (audio.duration || 0) - audio.currentTime;

        setAudioStates(prevState => ({
          ...prevState,
          [audioId]: {
            ...prevState[audioId],
            progress: isNaN(progress) ? 0 : progress,
            currentTime: audio.currentTime || 0,
            duration: audio.duration || 0,
            remainingTime: Math.max(0, remainingTime)
          }
        }));
      }
    }, 100); // Update every 100ms for smooth progress
  };

  // Stop progress tracking interval
  const stopProgressTracking = (audioId) => {
    if (progressIntervals.current[audioId]) {
      clearInterval(progressIntervals.current[audioId]);
      delete progressIntervals.current[audioId];
    }
  };

  const setupAudio = (audioId, url) => {
    // Create audio element if it doesn't exist
    if (!audioRefs.current[audioId]) {
      const audio = new Audio();

      // Add error handling
      audio.onerror = () => {
        console.error(`Error loading audio: ${url}`);
        setAudioStates(prevState => ({
          ...prevState,
          [audioId]: {
            ...prevState[audioId],
            isLoading: false,
            error: true,
            isPlaying: false
          }
        }));
        stopProgressTracking(audioId);
      };

      // Add ended event listener
      audio.addEventListener('ended', () => {
        setAudioStates(prevState => ({
          ...prevState,
          [audioId]: {
            ...prevState[audioId],
            isPlaying: false,
            progress: 0,
            currentTime: 0,
            remainingTime: prevState[audioId].duration || 0
          }
        }));
        stopProgressTracking(audioId);
        audio.currentTime = 0;
      });

      // Add loadedmetadata event to get duration
      audio.addEventListener('loadedmetadata', () => {
        setAudioStates(prevState => ({
          ...prevState,
          [audioId]: {
            ...prevState[audioId],
            duration: audio.duration || 0,
            remainingTime: audio.duration || 0
          }
        }));
      });

      // Set the audio source
      audio.src = url;
      audio.crossOrigin = "anonymous"; // Try adding CORS header
      audio.preload = "metadata";

      // Store in refs
      audioRefs.current[audioId] = audio;
    }

    return audioRefs.current[audioId];
  };

  const updateProgressBar = (audioId, percent) => {
    if (!audioRefs.current[audioId]) return;

    const audio = audioRefs.current[audioId];
    const newTime = (percent / 100) * (audio.duration || 0);
    const newRemainingTime = (audio.duration || 0) - newTime;

    // Update audio time
    if (!isNaN(newTime) && isFinite(newTime)) {
      audio.currentTime = newTime;

      // Update the state
      setAudioStates(prevState => ({
        ...prevState,
        [audioId]: {
          ...prevState[audioId],
          progress: percent / 100,
          currentTime: newTime,
          remainingTime: Math.max(0, newRemainingTime)
        }
      }));
    }
  };

  const togglePlay = (audioId, url) => {
    // Stop all other playing audio
    Object.keys(audioStates).forEach(id => {
      if (id !== audioId && audioStates[id]?.isPlaying && audioRefs.current[id]) {
        audioRefs.current[id].pause();
        audioRefs.current[id].currentTime = 0;

        // Reset state for stopped audio
        const audio = audioRefs.current[id];
        setAudioStates(prevState => ({
          ...prevState,
          [id]: {
            ...prevState[id],
            isPlaying: false,
            progress: 0,
            currentTime: 0,
            remainingTime: prevState[id].duration || 0
          }
        }));
        stopProgressTracking(id);
      }
    });

    const audio = setupAudio(audioId, url);
    const currentState = audioStates[audioId];

    if (currentState?.isPlaying) {
      // Pause currently playing audio
      audio.pause();
      setAudioStates(prevState => ({
        ...prevState,
        [audioId]: { ...prevState[audioId], isPlaying: false }
      }));
      stopProgressTracking(audioId);
    } else {
      // Set loading state before playing
      setAudioStates(prevState => ({
        ...prevState,
        [audioId]: {
          ...prevState[audioId],
          isLoading: true,
          error: false
        }
      }));

      // Try to play the audio
      audio.play()
        .then(() => {
          setAudioStates(prevState => ({
            ...prevState,
            [audioId]: {
              ...prevState[audioId],
              isLoading: false,
              isPlaying: true,
              // Set initial remaining time when starting playback
              remainingTime: (audio.duration || 0) - (audio.currentTime || 0)
            }
          }));
          startProgressTracking(audioId);
        })
        .catch(err => {
          console.error("Audio playback error:", err);
          setAudioStates(prevState => ({
            ...prevState,
            [audioId]: {
              ...prevState[audioId],
              isLoading: false,
              error: true,
              isPlaying: false
            }
          }));
          stopProgressTracking(audioId);
        });
    }
  };

  // Handle progress bar click/drag
  const handleProgressBarClick = (audioId, event) => {
    const progressBar = event.currentTarget;
    const rect = progressBar.getBoundingClientRect();
    const position = (event.clientX - rect.left) / progressBar.clientWidth;
    const percentProgress = Math.max(0, Math.min(100, position * 100));

    updateProgressBar(audioId, percentProgress);
  };

  // Get button content based on audio state
  const getButtonContent = (audioState) => {
    if (audioState?.isLoading) {
      return (
        <div className="animate-pulse flex items-center justify-center">
          <div className="w-3 h-3 bg-white rounded-full"></div>
        </div>
      );
    }

    if (audioState?.error) {
      return <AlertCircle size={16} />;
    }

    return audioState?.isPlaying ? <Pause size={16} /> : <Play size={16} />;
  };

  // Clean up audio instances and intervals on unmount
  useEffect(() => {
    return () => {
      // Clear all interval timers
      Object.keys(progressIntervals.current).forEach(id => {
        clearInterval(progressIntervals.current[id]);
      });

      // Clean up audio elements
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.currentTime = 0;
          audio.src = "";
        }
      });
    };
  }, []);

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[100000] p-1 overflow-y-auto">
      <div className="relative bg-[#1F2123] text-[#EAECEF] shadow-lg rounded-xl p-3 sm:p-6 w-full max-w-[94%] sm:max-w-4xl border border-[#3A3D3E] flex flex-col items-center max-h-[90vh]">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 sm:top-4 sm:right-4 text-white hover:text-gray-300 transition bg-[#2C2F30] p-1.5 sm:p-2 rounded-full z-50"
        >
          <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" className="sm:w-4 sm:h-4">
            <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>

        <h2 className="text-xl sm:text-2xl font-bold mb-3 sm:mb-6 text-center">Voice Cloning Samples</h2>

        {/* Speaker selector */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-6 justify-center">
          {speakers.map(speaker => (
            <button
              key={speaker.id}
              className={`px-2 sm:px-3 py-1 rounded-full text-xs sm:text-sm border transition ${
                selectedSpeaker === speaker.id
                  ? 'bg-[#178A94] border-[#178A94] text-white'
                  : 'border-[#3A3D3E] text-[#B0B4BB] hover:border-[#178A94]/50'
              }`}
              onClick={() => setSelectedSpeaker(speaker.id)}
            >
              {speaker.name}
            </button>
          ))}
        </div>

        {/* Table of samples */}
        <div className="w-full bg-[#2C2F30] rounded-xl p-2 sm:p-4 overflow-auto">
          <div className="min-w-full">
            <div className="border-b border-[#3A3D3E] hidden sm:flex">
              <div className="text-left py-3 px-2 sm:px-4 text-[#B0B4BB] font-semibold w-[25%]">Speaker</div>
              <div className="text-left py-3 px-2 sm:px-4 text-[#B0B4BB] font-semibold w-[37.5%]">Original Audio</div>
              <div className="text-left py-3 px-2 sm:px-4 text-[#B0B4BB] font-semibold w-[37.5%]">AI Generated Clone</div>
            </div>

            {speakers.filter(speaker => !selectedSpeaker || speaker.id === selectedSpeaker).map(speaker => (
              <div key={speaker.id} className="border-b border-[#3A3D3E]/40 last:border-0">
                {/* Mobile layout - stacked */}
                <div className="flex flex-col sm:hidden py-3">
                  <div className="px-3 mb-2">
                    <div className="font-medium text-[#EAECEF]">{speaker.name}</div>
                    <div className="text-xs text-[#B0B4BB]">10-second sample</div>
                  </div>

                  <div className="px-3 mb-3">
                    <div className="text-xs text-[#B0B4BB] mb-1 font-medium">Original Audio:</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePlay(`${speaker.id}-original`, speaker.originalUrl)}
                        className={`p-1.5 rounded-full flex-shrink-0 transition ${
                          audioStates[`${speaker.id}-original`]?.error
                            ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                            : 'bg-[#2C2F30] border border-[#178A94] hover:bg-[#178A94]/20'
                        }`}
                        disabled={audioStates[`${speaker.id}-original`]?.isLoading}
                      >
                        {getButtonContent(audioStates[`${speaker.id}-original`])}
                      </button>
                      <div
                        className="w-full bg-[#181A1B] h-2 rounded-full overflow-hidden cursor-pointer relative"
                        onClick={(e) => handleProgressBarClick(`${speaker.id}-original`, e)}
                      >
                        <div
                          className={`h-2 rounded-full ${
                            audioStates[`${speaker.id}-original`]?.error
                              ? 'bg-red-500/50'
                              : 'bg-[#178A94]'
                          }`}
                          style={{
                            width: `${(audioStates[`${speaker.id}-original`]?.progress || 0) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-[#B0B4BB] text-xs whitespace-nowrap min-w-[40px]">
                        {formatTime(audioStates[`${speaker.id}-original`]?.remainingTime || 0)}
                      </span>
                    </div>
                  </div>

                  <div className="px-3">
                    <div className="text-xs text-[#B0B4BB] mb-1 font-medium">AI Generated Clone:</div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => togglePlay(`${speaker.id}-cloned`, speaker.clonedUrl)}
                        className={`p-1.5 rounded-full flex-shrink-0 transition ${
                          audioStates[`${speaker.id}-cloned`]?.error
                            ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                            : 'bg-[#178A94] hover:bg-[#146E75]'
                        }`}
                        disabled={audioStates[`${speaker.id}-cloned`]?.isLoading}
                      >
                        {getButtonContent(audioStates[`${speaker.id}-cloned`])}
                      </button>
                      <div
                        className="w-full bg-[#181A1B] h-2 rounded-full overflow-hidden cursor-pointer relative"
                        onClick={(e) => handleProgressBarClick(`${speaker.id}-cloned`, e)}
                      >
                        <div
                          className={`h-2 rounded-full ${
                            audioStates[`${speaker.id}-cloned`]?.error
                              ? 'bg-red-500/50'
                              : 'bg-[#178A94]'
                          }`}
                          style={{
                            width: `${(audioStates[`${speaker.id}-cloned`]?.progress || 0) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-[#B0B4BB] text-xs whitespace-nowrap min-w-[40px]">
                        {formatTime(audioStates[`${speaker.id}-cloned`]?.remainingTime || 0)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Desktop layout - table row */}
                <div className="hidden sm:flex py-4">
                  <div className="w-[25%] px-4">
                    <div className="font-medium text-[#EAECEF]">{speaker.name}</div>
                    <div className="text-xs text-[#B0B4BB] mt-1">10-second sample</div>
                  </div>
                  <div className="w-[37.5%] px-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => togglePlay(`${speaker.id}-original`, speaker.originalUrl)}
                        className={`p-2 rounded-full flex-shrink-0 transition ${
                          audioStates[`${speaker.id}-original`]?.error
                            ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                            : 'bg-[#2C2F30] border border-[#178A94] hover:bg-[#178A94]/20'
                        }`}
                        disabled={audioStates[`${speaker.id}-original`]?.isLoading}
                      >
                        {getButtonContent(audioStates[`${speaker.id}-original`])}
                      </button>
                      <div
                        className="w-full bg-[#181A1B] h-2 rounded-full overflow-hidden cursor-pointer relative"
                        onClick={(e) => handleProgressBarClick(`${speaker.id}-original`, e)}
                      >
                        <div
                          className={`h-2 rounded-full ${
                            audioStates[`${speaker.id}-original`]?.error
                              ? 'bg-red-500/50'
                              : 'bg-[#178A94]'
                          }`}
                          style={{
                            width: `${(audioStates[`${speaker.id}-original`]?.progress || 0) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-[#B0B4BB] text-sm whitespace-nowrap min-w-[48px]">
                        {formatTime(audioStates[`${speaker.id}-original`]?.remainingTime || 0)}
                      </span>
                    </div>
                    {audioStates[`${speaker.id}-original`]?.error && (
                      <div className="text-xs text-red-400 mt-1">
                        Failed to load audio. The URL may be invalid or not accessible.
                      </div>
                    )}
                  </div>
                  <div className="w-[37.5%] px-4">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => togglePlay(`${speaker.id}-cloned`, speaker.clonedUrl)}
                        className={`p-2 rounded-full flex-shrink-0 transition ${
                          audioStates[`${speaker.id}-cloned`]?.error
                            ? 'bg-red-500/20 border border-red-500/50 text-red-400'
                            : 'bg-[#178A94] hover:bg-[#146E75]'
                        }`}
                        disabled={audioStates[`${speaker.id}-cloned`]?.isLoading}
                      >
                        {getButtonContent(audioStates[`${speaker.id}-cloned`])}
                      </button>
                      <div
                        className="w-full bg-[#181A1B] h-2 rounded-full overflow-hidden cursor-pointer relative"
                        onClick={(e) => handleProgressBarClick(`${speaker.id}-cloned`, e)}
                      >
                        <div
                          className={`h-2 rounded-full ${
                            audioStates[`${speaker.id}-cloned`]?.error
                              ? 'bg-red-500/50'
                              : 'bg-[#178A94]'
                          }`}
                          style={{
                            width: `${(audioStates[`${speaker.id}-cloned`]?.progress || 0) * 100}%`
                          }}
                        ></div>
                      </div>
                      <span className="text-[#B0B4BB] text-sm whitespace-nowrap min-w-[48px]">
                        {formatTime(audioStates[`${speaker.id}-cloned`]?.remainingTime || 0)}
                      </span>
                    </div>
                    {audioStates[`${speaker.id}-cloned`]?.error && (
                      <div className="text-xs text-red-400 mt-1">
                        Failed to load audio. The URL may be invalid or not accessible.
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-3 sm:mt-6 text-xs sm:text-sm text-[#B0B4BB] text-center max-w-2xl px-2">
          These samples demonstrate our voice cloning technology using just 10 seconds of audio input.
          The quality of cloned voices may vary depending on the clarity and quality of the original recording.
        </p>
      </div>
    </div>
  );
};

const VoiceCloning = ({ onVoiceAdded = () => {}, onVoiceDeleted = () => {} }) => {
  const { user } = useAuth();
  const [isPremiumUser, setIsPremiumUser] = useState(false);
  const [isLoadingPremium, setIsLoadingPremium] = useState(true);
  const [showSamplesModal, setShowSamplesModal] = useState(false);
  const [privateVoices, setPrivateVoices] = useState([]);
  const [isLoadingVoices, setIsLoadingVoices] = useState(false);
  const [playingPreviewId, setPlayingPreviewId] = useState(null);
  const [showAddVoiceModal, setShowAddVoiceModal] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [voiceToDelete, setVoiceToDelete] = useState(null);
  const audioRefs = useRef({});
  const MAX_VOICES = 5;

  // Set all users as premium users for voice cloning
  useEffect(() => {
    // Set everyone as premium now, regardless of actual premium status
    setIsPremiumUser(true);
    setIsLoadingPremium(false);
  }, [user]);

  // Fetch private voices for all users
  useEffect(() => {
    if (!user || !user.email) return;

    const fetchPrivateVoices = async () => {
      setIsLoadingVoices(true);

      try {
        const privateVoicesRef = doc(db, 'private_voices', user.email);
        const docSnap = await getDoc(privateVoicesRef);

        if (docSnap.exists()) {
          const voicesData = docSnap.data().voices || [];
          setPrivateVoices(voicesData);
        } else {
          // No document exists yet - empty array
          setPrivateVoices([]);
        }
      } catch (error) {
        console.error('Error fetching private voices:', error);
        // Just set empty array on error
        setPrivateVoices([]);
      } finally {
        setIsLoadingVoices(false);
      }
    };

    fetchPrivateVoices();
  }, [user]);

  // Handle preview play
  const handlePreviewPlay = (voiceId, previewUrl, voiceName) => {
    // Stop any currently playing audio
    if (playingPreviewId && audioRefs.current[playingPreviewId]) {
      audioRefs.current[playingPreviewId].pause();
      audioRefs.current[playingPreviewId].currentTime = 0;
    }

    // If clicking the same voice that's already playing, stop it
    if (playingPreviewId === voiceId) {
      setPlayingPreviewId(null);
      return;
    }

    // Play the new audio
    if (!audioRefs.current[voiceId]) {
      audioRefs.current[voiceId] = new Audio(previewUrl);
    }
    
    const audio = audioRefs.current[voiceId];
    audio.play().then(() => {
      setPlayingPreviewId(voiceId);
    }).catch(err => {
      console.error("Audio playback error:", err);
    });

    // Listen for when audio ends
    audio.onended = () => {
      setPlayingPreviewId(null);
    };
  };

  // Handle delete voice (move to archived_voices)
  const confirmDeleteVoice = (voice) => {
    setVoiceToDelete(voice);
    setShowDeleteConfirmation(true);
  };

  const handleDeleteVoice = async () => {
    if (!voiceToDelete || !user || !user.email) return;

    try {
      // 1. Update local state immediately for better UX
      const deletedVoiceId = voiceToDelete.id;
      setPrivateVoices(prev => prev.filter(voice => voice.id !== deletedVoiceId));

      // 2. Close confirmation dialog
      setShowDeleteConfirmation(false);
      setVoiceToDelete(null);

      try {
        // 3. Call Azure function to delete the voice
        const deleteResponse = await fetch(
          "https://voiceops.azurewebsites.net/api/deletevoice?code=st7bF8Rwc1ErqTdZd8WDLp2dBhBrlByjqZ2y67jb_LlGAzFulzdkfQ==",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json"
            },
            body: JSON.stringify({
              userId: user.uid,
              userEmail: user.email,
              voiceId: deletedVoiceId,
              name: voiceToDelete['Display Name'] || voiceToDelete.name
            })
          }
        );

        if (!deleteResponse.ok) {
          throw new Error(`Failed to delete voice: ${deleteResponse.status} ${deleteResponse.statusText}`);
        }

        const deleteResult = await deleteResponse.json();

        // The Azure function handles removing from Firestore, but we'll also do
        // the Firestore operations as a backup/fallback with improved error handling
        try {
          // Handle private voices and archived voices as separate operations
          // so one failing doesn't prevent the other

          // 1. Handle private voices update - remove the deleted voice
          try {
            const privateVoicesRef = doc(db, 'private_voices', user.email);
            const privateVoicesSnap = await getDoc(privateVoicesRef);
            let currentVoices = [];

            if (privateVoicesSnap.exists()) {
              currentVoices = privateVoicesSnap.data().voices || [];
            }

            // Remove the voice to delete
            const updatedVoices = currentVoices.filter(voice => voice.id !== deletedVoiceId);

            // Update private_voices document
            await setDoc(privateVoicesRef, { voices: updatedVoices }, { merge: true });
          } catch (privateError) {
            console.error('Error updating private voices:', privateError);
            // Continue with archiving even if removing from private voices fails
          }

          // 2. Handle archiving - add to archived voices collection
          try {
            // Create archived voices document if it doesn't exist
            const archivedVoicesRef = doc(db, 'archived_voices', user.email);

            // Use a direct approach to avoid potential permission issues
            await setDoc(archivedVoicesRef, {
              lastUpdated: serverTimestamp(),
              // Add the deleted voice directly into a new array field
              archivedItems: [{
                voice: voiceToDelete,
                id: deletedVoiceId,
                name: voiceToDelete['Display Name'] || voiceToDelete.name,
                archivedDate: new Date().toISOString() // Use ISO string instead of serverTimestamp in array
              }]
            }, { merge: true }); // Use merge to add to existing data
          } catch (archiveError) {
            console.error('Error archiving voice:', archiveError);
            // This is just a backup, so continue even if it fails
          }

          // Call the callback to notify about voice deletion regardless
          onVoiceDeleted();
        } catch (firestoreError) {
          console.error('Error updating Firestore when deleting voice:', firestoreError);
          // Azure function already handled the deletion, so this is just a backup
        }

      } catch (error) {
        console.error('Error deleting voice with Azure function:', error);
        alert(`Failed to delete voice: ${error.message}. Please try again.`);

        // Try to revert the UI state since the deletion failed
        try {
          setPrivateVoices(prev => {
            if (!prev.some(voice => voice.id === voiceToDelete.id)) {
              return [...prev, voiceToDelete];
            }
            return prev;
          });
        } catch (uiError) {
          console.error('Failed to revert UI state:', uiError);
        }
      }
    } catch (error) {
      console.error('Error in delete voice process:', error);
      alert('Failed to delete voice. Please try again.');
    }
  };

  // Clean up audio elements on unmount
  useEffect(() => {
    return () => {
      Object.values(audioRefs.current).forEach(audio => {
        if (audio) {
          audio.pause();
          audio.onended = null;
        }
      });
      audioRefs.current = {};
    };
  }, []);

  if (isLoadingPremium) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <div className="animate-pulse text-[#178A94]">Loading...</div>
      </div>
    );
  }

  // Non-premium interface
  if (!isPremiumUser) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center p-8">
        <div className="bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-8 text-center max-w-2xl">
          <div className="flex justify-center mb-6">
            <div className="p-4 bg-[#178A94]/20 rounded-full">
              <Lock size={48} className="text-[#178A94]" />
            </div>
          </div>
          <h2 className="text-2xl font-bold text-[#EAECEF] mb-4">Voice Cloning</h2>
          <p className="text-[#B0B4BB] mb-6">
            Purchase our <span className="inline-flex items-center gap-0 justify-center"><IndianRupee size={16}/>449 </span> plan or above to access this feature.
            Create a <span className="font-bold text-[#EAECEF]">100% copyright-free</span> and <span className="font-bold text-[#EAECEF]">unique voice</span> for your brand according to your specific use case.
            With just <span className="font-bold text-[#EAECEF]">10 seconds</span> of audio, our technology captures your voice features and creates a <span className="font-bold text-[#EAECEF]">custom voice </span>
            that sets your brand apart while ensuring <span className="font-bold text-[#EAECEF]">complete legal ownership</span>.
          </p>
          <button 
            onClick={() => setShowSamplesModal(true)}
            className="flex items-center justify-center mx-auto gap-2 px-4 py-2 bg-[#178A94] text-white rounded-lg hover:bg-[#146E75] transition"
          >
            <ExternalLink size={18} />
            Check Out Our Samples
          </button>
        </div>

        {showSamplesModal && <SamplesModal onClose={() => setShowSamplesModal(false)} />}
      </div>
    );
  }

  // Loading premium voices
  if (isLoadingVoices) {
    return (
      <div className="w-full h-full flex items-center justify-center p-8">
        <div className="animate-pulse text-[#178A94]">Loading your voice collection...</div>
      </div>
    );
  }

  // Premium interface
  return (
    <div className="w-full h-full flex flex-col p-4 sm:p-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-[#EAECEF]">Voice Cloning Studio</h2>
        <p className="text-[#B0B4BB] mt-2">
          Create and manage 100% copyright-free, unique voices for your brand. Each voice is custom-made
          for your specific use case, ensuring your brand stands out while maintaining complete legal ownership.
          You can have up to {MAX_VOICES} voices at a time.
        </p>
      </div>

      {/* Voice list or empty state */}
      {privateVoices.length === 0 ? (
        <div className="flex-1 flex flex-col items-center justify-center">
          <div className="bg-[#2C2F30] rounded-xl shadow-lg border border-[#3A3D3E] p-8 text-center max-w-2xl">
            <div className="flex justify-center mb-6">
              <div className="p-4 bg-[#178A94]/20 rounded-full">
                <Mic2 size={48} className="text-[#178A94]" />
              </div>
            </div>
            <h3 className="text-xl font-bold text-[#EAECEF] mb-4">No Cloned Voices Yet</h3>
            <p className="text-[#B0B4BB] mb-6">
              You haven't cloned any voices yet. Create your first voice clone to get started.
            </p>
            <button
              onClick={() => setShowAddVoiceModal(true)}
              className="flex items-center justify-center mx-auto gap-2 px-6 py-3 bg-[#178A94] text-white rounded-lg hover:bg-[#146E75] transition"
            >
              <Plus size={20} />
              Create Voice Clone
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 px-1 sm:px-0 pb-32 sm:pb-16 mb-24 sm:mb-12">
            {privateVoices.map((voice) => (
              <div
                key={voice.id}
                className="bg-[#2C2F30] rounded-lg border border-[#3A3D3E] overflow-hidden"
              >
                <div className="p-4">
                  <div className="flex justify-between items-start">
                    <h3 className="font-bold text-[#EAECEF] text-lg">{voice['Display Name'] || voice.name}</h3>
                    <button
                      onClick={() => confirmDeleteVoice(voice)}
                      className="p-1 text-red-400 hover:text-red-500 transition-colors"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                  <p className="text-[#B0B4BB] text-sm mt-1">
                    {voice.description || "Professionally Cloned Voice"}
                  </p>
                </div>

                {/* Preview playback section */}
                <div className="px-4 py-3 bg-[#181A1B] flex items-center">
                  <button
                    onClick={() => handlePreviewPlay(voice.id, voice.preview_link, voice['Display Name'] || voice.name)}
                    className={`p-2 rounded-full mr-3 ${
                      playingPreviewId === voice.id
                        ? 'bg-red-500 text-white'
                        : 'bg-[#178A94] text-white hover:bg-[#146E75]'
                    }`}
                  >
                    {playingPreviewId === voice.id ? <Pause size={16} /> : <Play size={16} />}
                  </button>
                  <div className="text-sm text-[#B0B4BB]">Preview Sample</div>
                </div>
              </div>
            ))}

            {/* Add new voice card - only show if under the maximum */}
            {privateVoices.length < MAX_VOICES && (
              <div
                onClick={() => setShowAddVoiceModal(true)}
                className="bg-[#2C2F30] rounded-lg border border-dashed border-[#178A94] flex items-center justify-center cursor-pointer hover:bg-[#3A3D3E] transition-colors"
                style={{ minHeight: '180px' }}
              >
                <div className="text-center p-4">
                  <div className="flex justify-center mb-3">
                    <div className="p-3 bg-[#178A94]/20 rounded-full">
                      <Plus size={32} className="text-[#178A94]" />
                    </div>
                  </div>
                  <p className="text-[#B0B4BB]">Create New Voice Clone</p>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Delete confirmation modal */}
      {showDeleteConfirmation && voiceToDelete && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-[#2C2F30] rounded-lg border border-[#3A3D3E] p-6 max-w-md">
            <h3 className="text-xl font-bold text-[#EAECEF] mb-4">Confirm Delete</h3>
            <p className="text-[#B0B4BB] mb-6">
              Are you sure you want to delete "{voiceToDelete['Display Name'] || voiceToDelete.name}"? 
              This voice will be moved to archived voices and can be restored later.
            </p>
            <div className="flex justify-end gap-4">
              <button
                onClick={() => {
                  setShowDeleteConfirmation(false);
                  setVoiceToDelete(null);
                }}
                className="px-4 py-2 border border-[#3A3D3E] text-[#EAECEF] rounded hover:bg-[#3A3D3E] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteVoice}
                className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add voice modal component */}
      {showAddVoiceModal && (
        <VoiceCloningModal 
          onClose={() => setShowAddVoiceModal(false)}
          onSuccess={(voiceData) => {
            // Add the new voice to the list and close the modal
            setPrivateVoices(prev => [...prev, voiceData]);
            setShowAddVoiceModal(false);

            // Notify parent about the new voice being added
            onVoiceAdded();
          }}
          user={user}
        />
      )}
    </div>
  );
};

export default VoiceCloning;